import React, {
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { CalendarIcon } from '@src/Components/Icons';

import BackgroundFixed from './BackgroundFixed/BackgroundFixed';
import Calendar from './Calendar/Calendar';
import styles from './InputDate.module.scss';
import { IInput } from './InputDate.type';
const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];
export interface IDatepicker extends IInput {
  disableDate?: (date: Date) => boolean;
  disabledMonth?: boolean;
  disabledYear?: boolean;
}

const InputDate: React.FC<IDatepicker> = ({
  label,
  value,
  onChange,
  name,
  error,
  variant = 'general-line',
  disabled = false,
  customStyleGroupChoice,
  calendarIsShown,
  setCalendarIsShown,
  placeholder,
  disabledMonth,
  disabledYear
}) => {
  const elRef = useRef<HTMLInputElement>(null);

  const [isCalShow, setIsCalShow] = useState<boolean>(false);
  const [currentValue, setCurrentValue] = useState<string>('');

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCurrentValue(event.target.value);
    onChange?.(event);
  };

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  useEffect(() => {
    if (calendarIsShown !== undefined) {
      setIsCalShow(calendarIsShown);
    }
  }, [calendarIsShown]);

  useEffect(() => {
    if (setCalendarIsShown !== undefined) {
      setCalendarIsShown(isCalShow);
    }
  }, [isCalShow, setCalendarIsShown]);

  const formattedValue = useMemo(() => {
    if (!currentValue) {
      return undefined;
    }
    const inputDate = currentValue;
    const [year, month, day] = inputDate.split('-');
    return `${day} ${months[Number(month) - 1]} ${year}`;
  }, [currentValue]);

  return (
    <div className={styles['Container']} data-testid="input-area">
      <span>{label}</span>
      <div className={styles['container-date']}>
        <div
          className={`${styles['InputContainer']} ${
            styles[`${error ? 'error' : ''}`]
          }`}
        >
          <input
            ref={elRef}
            readOnly
            autoComplete="off"
            type="text"
            onChange={handleChange}
            value={formattedValue}
            placeholder={placeholder}
            name={name}
            disabled={disabled}
            onClick={() => setIsCalShow(true)}
          />

          <span
            className={styles['open-button']}
            onClick={() => setIsCalShow(true)}
          >
            <button type="button">
              <CalendarIcon color="#FFFFFF" />
            </button>
          </span>
          {isCalShow && (
            <>
              <BackgroundFixed onClick={() => setIsCalShow(false)} />
              <Calendar
                customStyleGroupChoice={customStyleGroupChoice}
                value={value}
                variant={variant}
                onCancel={() => setIsCalShow(false)}
                onOk={(result) => {
                  setCurrentValue(result);
                  onChange?.({
                    currentTarget: {
                      value: result,
                      name: name
                    }
                  });
                  setIsCalShow(false);
                }}
                disabledMonth={disabledMonth}
                disabledYear={disabledYear}
              />
            </>
          )}
        </div>
      </div>
      {error && (
        <span className={styles['errors']}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M8.00017 9.33337C7.83535 9.33337 7.67423 9.38225 7.53719 9.47382C7.40015 9.56538 7.29334 9.69553 7.23027 9.84781C7.16719 10.0001 7.15069 10.1676 7.18284 10.3293C7.215 10.4909 7.29437 10.6394 7.41091 10.756C7.52745 10.8725 7.67594 10.9519 7.83759 10.984C7.99924 11.0162 8.1668 10.9997 8.31907 10.9366C8.47134 10.8735 8.60149 10.7667 8.69306 10.6297C8.78462 10.4926 8.8335 10.3315 8.8335 10.1667C8.8335 9.94569 8.7457 9.73373 8.58942 9.57745C8.43314 9.42117 8.22118 9.33337 8.00017 9.33337ZM8.00017 8.33337C8.17698 8.33337 8.34655 8.26314 8.47157 8.13811C8.59659 8.01309 8.66683 7.84352 8.66683 7.66671V5.66671C8.66683 5.4899 8.59659 5.32033 8.47157 5.1953C8.34655 5.07028 8.17698 5.00004 8.00017 5.00004C7.82335 5.00004 7.65379 5.07028 7.52876 5.1953C7.40374 5.32033 7.3335 5.4899 7.3335 5.66671V7.66671C7.3335 7.84352 7.40374 8.01309 7.52876 8.13811C7.65379 8.26314 7.82335 8.33337 8.00017 8.33337ZM8.00017 1.33337C6.68162 1.33337 5.39269 1.72437 4.29636 2.45691C3.20004 3.18945 2.34555 4.23064 1.84097 5.44882C1.33638 6.66699 1.20436 8.00744 1.4616 9.30064C1.71883 10.5938 2.35377 11.7817 3.28612 12.7141C4.21847 13.6464 5.40636 14.2814 6.69956 14.5386C7.99277 14.7958 9.33321 14.6638 10.5514 14.1592C11.7696 13.6547 12.8108 12.8002 13.5433 11.7038C14.2758 10.6075 14.6668 9.31858 14.6668 8.00004C14.6649 6.23253 13.9619 4.53796 12.7121 3.28814C11.4622 2.03832 9.76768 1.33532 8.00017 1.33337ZM8.00017 13.3334C6.94533 13.3334 5.91419 13.0206 5.03712 12.4345C4.16006 11.8485 3.47648 11.0156 3.07281 10.041C2.66914 9.06648 2.56352 7.99412 2.76931 6.95956C2.9751 5.92499 3.48305 4.97468 4.22893 4.2288C4.97481 3.48292 5.92512 2.97497 6.95968 2.76919C7.99425 2.5634 9.0666 2.66902 10.0411 3.07268C11.0157 3.47635 11.8486 4.15994 12.4347 5.037C13.0207 5.91406 13.3335 6.94521 13.3335 8.00004C13.3317 9.41399 12.7693 10.7695 11.7695 11.7693C10.7696 12.7691 9.41411 13.3316 8.00017 13.3334Z"
              fill="#B61733"
            />
          </svg>
          <div className={styles['ErrorMessage']}>{error}</div>
        </span>
      )}
    </div>
  );
};

export default InputDate;
